import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export type StatusType = 'good' | 'poor';
const ALL_STATUS_TYPES: StatusType[] = ['good', 'poor'];

interface UIState {
  // タブ状態
  activeTab: string;
  setActiveTab: (tab: string) => void;

  // スクロール位置（ルートパスごとに保存）
  scrollPositions: Record<string, number>;
  saveScrollPosition: (route: string, position: number) => void;
  getScrollPosition: (route: string) => number;

  // フィルター状態
  filters: {
    search: string;
    selectedStoreIds: string[]; // 店舗IDで管理（空配列は「0店舗選択」を意味する）
    dateRange: { from: Date; to: Date } | null;
    statusTypes: StatusType[]; // 営業ステータスフィルター
  };
  setFilters: (filters: Partial<UIState['filters']>) => void;
  resetFilters: () => void;

  // 店舗フィルターの初期化状態
  // true: ユーザーが一度でも dashboard を開いた（= selectedStoreIds は信頼できる）
  // false: 未初期化（= fetched 直後の allStoreIds で初期化する）
  storeFilterInitialized: boolean;

  // 店舗フィルター操作
  toggleStoreFilter: (storeId: string) => void;
  initializeStoreFilters: (storeIds: string[]) => void;
}

const defaultFilters = {
  search: '',
  selectedStoreIds: [] as string[], // 空の場合は全店舗表示
  dateRange: null as { from: Date; to: Date } | null,
  statusTypes: ['good', 'poor'] as StatusType[], // デフォルトは全ステータス
};

export const useUIStore = create<UIState>()(
  persist(
    (set, get) => ({
      // タブ状態
      activeTab: 'cards',
      setActiveTab: (tab) => set({ activeTab: tab }),

      // スクロール位置
      scrollPositions: {},
      saveScrollPosition: (route, position) =>
        set((state) => ({
          scrollPositions: { ...state.scrollPositions, [route]: position },
        })),
      getScrollPosition: (route) => get().scrollPositions[route] || 0,

      // フィルター状態
      filters: defaultFilters,
      storeFilterInitialized: false,
      setFilters: (filters) =>
        set((state) => ({
          filters: { ...state.filters, ...filters },
        })),
      resetFilters: () => set({ filters: defaultFilters }),

      // 店舗フィルター操作
      toggleStoreFilter: (storeId) =>
        set((state) => {
          const currentIds = state.filters.selectedStoreIds;
          const newIds = currentIds.includes(storeId)
            ? currentIds.filter((id) => id !== storeId)
            : [...currentIds, storeId];
          return {
            filters: { ...state.filters, selectedStoreIds: newIds },
          };
        }),
      initializeStoreFilters: (storeIds) =>
        set((state) => {
          if (state.storeFilterInitialized) {
            // 初期化済みでも、persist された selectedStoreIds に assignedStoreIds 範囲外の ID が
            // 紛れていれば排除する。ホステス切替や help_shifts 期間終了で発生し得る汚染対策。
            const validSelected = state.filters.selectedStoreIds.filter((id) =>
              storeIds.includes(id)
            );
            if (validSelected.length === state.filters.selectedStoreIds.length) {
              return state;
            }
            return {
              filters: { ...state.filters, selectedStoreIds: validSelected },
            };
          }
          // 未初期化: 既存選択は assignedStoreIds 範囲内のみ維持、無ければ全店舗
          // (初期化済み分岐と同じ intersect ロジックを適用し、状態を常に交差済みに保つ)
          const validExisting = state.filters.selectedStoreIds.filter((id) =>
            storeIds.includes(id)
          );
          const selectedStoreIds = validExisting.length > 0
            ? validExisting
            : storeIds;
          return {
            filters: { ...state.filters, selectedStoreIds },
            storeFilterInitialized: true,
          };
        }),
    }),
    {
      name: 'hostess-ui-state',
      version: 2,
      partialize: (state) => ({
        activeTab: state.activeTab,
        scrollPositions: state.scrollPositions,
        storeFilterInitialized: state.storeFilterInitialized,
        filters: {
          ...state.filters,
          // dateRange と search は揮発（リロードでリセット）
          // Date のシリアライズ問題回避 + 前回の絞り込みが残り続ける問題防止
          dateRange: null,
          search: '',
        },
      }),
      migrate: (persisted: any, version: number) => {
        let next = persisted;
        if (version === 0) {
          // v0→v1: 旧statusTypes (good/warning/danger) → 新形式 (good/poor) にリセット
          next = {
            ...next,
            filters: {
              ...next?.filters,
              statusTypes: [...ALL_STATUS_TYPES],
            },
          };
        }
        if (version < 2) {
          // v1→v2: storeFilterInitialized フラグを追加
          // 既存ユーザーで selectedStoreIds に値があれば初期化済みとみなす
          const hasSelections = (next?.filters?.selectedStoreIds?.length ?? 0) > 0;
          next = {
            ...next,
            storeFilterInitialized: hasSelections,
          };
        }
        return next;
      },
    }
  )
);
